import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Icons } from './Icons';
import { db, auth } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { updatePassword, signInWithEmailAndPassword } from 'firebase/auth';
import './LoginPage.css';

const SECURITY_QUESTIONS = [
  "What was your first pet's name?",
  "What city were you born in?",
  "What is your mother's maiden name?",
  "What was the name of your first school?",
  "What is your favorite movie?",
  "What street did you grow up on?"
];

export default function LoginPage() {
  const { signInWithGoogle, signUpWithEmail, signInWithEmail, createProfile, checkUsernameAvailable } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get('mode') === 'signup' ? 'signup' : 'login');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);

  // Login
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Signup
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Profile setup
  const [firstName, setFirstName] = useState('');
  const [username, setUsername] = useState('');
  const [usernameStatus, setUsernameStatus] = useState(null);
  const [securityQuestion, setSecurityQuestion] = useState(SECURITY_QUESTIONS[0]);
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [pin, setPin] = useState('');

  // Forgot password
  const [forgotStep, setForgotStep] = useState(0); // 0=email, 1=verify, 2=new password
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotAnswer, setForgotAnswer] = useState('');
  const [forgotPin, setForgotPin] = useState('');
  const [forgotProfile, setForgotProfile] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  useEffect(() => {
    const titles = { login: 'Log In | TrackThrough', signup: 'Sign Up | TrackThrough', setup: 'Complete Setup | TrackThrough', forgot: 'Recover Account | TrackThrough' };
    document.title = titles[mode] || 'TrackThrough';
  }, [mode]);

  useEffect(() => {
    if (!username) { setUsernameStatus(null); return; }
    if (username.length < 3 || username.length > 30 || /\s/.test(username) || !/^[a-zA-Z0-9_]+$/.test(username)) {
      setUsernameStatus('invalid'); return;
    }
    setUsernameStatus('checking');
    const timer = setTimeout(async () => {
      const available = await checkUsernameAvailable(username);
      setUsernameStatus(available ? 'available' : 'taken');
    }, 500);
    return () => clearTimeout(timer);
  }, [username, checkUsernameAvailable]);

  async function handleGoogleSignIn() {
    setLoading(true);
    try {
      const result = await signInWithGoogle();
      if (result.isNew) {
        setPendingUser(result.user);
        setFirstName(result.user.displayName?.split(' ')[0] || '');
        setMode('setup');
      } else {
        toast('Welcome back!');
      }
    } catch (err) { toast(err.message, 'error'); }
    setLoading(false);
  }

  async function handleEmailLogin(e) {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      await signInWithEmail(email, password);
      toast('Welcome back!');
    } catch (err) {
      toast(err.code === 'auth/invalid-credential' ? 'Invalid email or password' : err.message, 'error');
    }
    setLoading(false);
  }

  async function handleEmailSignup(e) {
    e.preventDefault();
    if (signupPassword !== confirmPassword) { toast('Passwords do not match', 'error'); return; }
    if (signupPassword.length < 6) { toast('Password must be at least 6 characters', 'error'); return; }
    setLoading(true);
    try {
      const user = await signUpWithEmail(signupEmail, signupPassword);
      setPendingUser(user);
      setMode('setup');
    } catch (err) {
      toast(err.code === 'auth/email-already-in-use' ? 'Email already in use. Try logging in.' : err.message, 'error');
    }
    setLoading(false);
  }

  async function handleProfileSetup(e) {
    e.preventDefault();
    if (usernameStatus !== 'available') { toast('Please choose a valid, available username', 'error'); return; }
    if (!firstName.trim()) { toast('First name is required', 'error'); return; }
    if (!securityAnswer.trim()) { toast('Please answer the security question', 'error'); return; }
    if (!/^\d{3}$/.test(pin)) { toast('PIN must be exactly 3 digits', 'error'); return; }
    setLoading(true);
    try {
      await createProfile(pendingUser.uid, pendingUser.email, {
        username: username.trim(), displayName: pendingUser.displayName || firstName,
        firstName: firstName.trim(), securityQuestion, securityAnswer: securityAnswer.trim(), pin
      });
      toast('Account created! Welcome to TrackThrough');
    } catch (err) { toast(err.message, 'error'); }
    setLoading(false);
  }

  // Forgot password
  async function handleForgotLookup(e) {
    e.preventDefault();
    if (!forgotEmail) return;
    setLoading(true);
    try {
      const q = query(collection(db, 'users'), where('email', '==', forgotEmail.trim().toLowerCase()));
      const snap = await getDocs(q);
      if (snap.empty) { toast('No account found with this email', 'error'); setLoading(false); return; }
      setForgotProfile(snap.docs[0].data());
      setForgotStep(1);
    } catch (err) { toast(err.message, 'error'); }
    setLoading(false);
  }

  async function handleForgotVerify(e) {
    e.preventDefault();
    if (!forgotProfile) return;
    if (forgotAnswer.trim().toLowerCase() === forgotProfile.securityAnswer?.toLowerCase() && forgotPin === forgotProfile.pin) {
      setForgotStep(2);
      toast('Identity verified! Set your new password.');
    } else {
      toast('Incorrect answer or PIN', 'error');
    }
  }

  async function handleSetNewPassword(e) {
    e.preventDefault();
    if (newPassword.length < 6) { toast('Password must be at least 6 characters', 'error'); return; }
    if (newPassword !== confirmNewPassword) { toast('Passwords do not match', 'error'); return; }
    setLoading(true);
    try {
      // Sign in with a temporary admin method isn't possible client-side
      // Instead we use Firebase's signInWithEmailAndPassword then updatePassword
      // But we don't know the old password... So we use a workaround:
      // We'll send a password reset email as fallback, but primarily
      // we allow the user to sign in fresh after verifying identity
      
      // Firebase requires recent auth to change password
      // Best approach: use sendPasswordResetEmail
      const { sendPasswordResetEmail } = await import('firebase/auth');
      await sendPasswordResetEmail(auth, forgotProfile.email);
      toast('Password reset link sent to your email. Use it to set your new password.');
      setForgotStep(3);
    } catch (err) { toast(err.message, 'error'); }
    setLoading(false);
  }

  return (
    <div className="login-page">
      <div className="login-bg"><div className="login-bg-pattern" /></div>

      <div className="login-container fade-in">
        <div className="login-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <div className="logo-mark">T T</div>
          <h1 className="logo-text">TrackThrough</h1>
          <p className="logo-tagline">The difference between applied and hired</p>
        </div>

        {/* ─── Login ─────────────────────────────────── */}
        {mode === 'login' && (
          <div className="login-card slide-up">
            <h2>Welcome Back</h2>
            <button className="google-btn" onClick={handleGoogleSignIn} disabled={loading}>
              <Icons.Google /> <span>Continue with Google</span>
            </button>
            <div className="divider"><span>or sign in with email</span></div>
            <form onSubmit={handleEmailLogin}>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required />
              </div>
              <div className="form-group">
                <label>Password</label>
                <div className="password-input">
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••" required />
                  <button type="button" className="pw-toggle" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <Icons.EyeOff size={16} /> : <Icons.Eye size={16} />}
                  </button>
                </div>
              </div>
              <button type="button" className="forgot-link" onClick={() => setMode('forgot')}>Forgot password?</button>
              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? <span className="loading-spinner" /> : 'Sign In'}
              </button>
            </form>
            <p className="switch-mode">Don't have an account? <button onClick={() => setMode('signup')}>Sign Up</button></p>
          </div>
        )}

        {/* ─── Signup ────────────────────────────────── */}
        {mode === 'signup' && (
          <div className="login-card slide-up">
            <h2>Create Account</h2>
            <button className="google-btn" onClick={handleGoogleSignIn} disabled={loading}>
              <Icons.Google /> <span>Continue with Google</span>
            </button>
            <div className="divider"><span>or sign up with email</span></div>
            <form onSubmit={handleEmailSignup}>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={signupEmail} onChange={e => setSignupEmail(e.target.value)} placeholder="you@example.com" required />
              </div>
              <div className="form-group">
                <label>Password <span className="hint">(min 6 characters)</span></label>
                <div className="password-input">
                  <input type={showPassword ? 'text' : 'password'} value={signupPassword} onChange={e => setSignupPassword(e.target.value)} placeholder="••••••" minLength={6} required />
                  <button type="button" className="pw-toggle" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <Icons.EyeOff size={16} /> : <Icons.Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Confirm Password</label>
                <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••" required />
              </div>
              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? <span className="loading-spinner" /> : 'Create Account'}
              </button>
            </form>
            <p className="switch-mode">Already have an account? <button onClick={() => setMode('login')}>Sign In</button></p>
          </div>
        )}

        {/* ─── Profile Setup ─────────────────────────── */}
        {mode === 'setup' && (
          <div className="login-card slide-up">
            <h2>Complete Your Profile</h2>
            <p className="setup-subtitle">Just a few more details to get started</p>
            <form onSubmit={handleProfileSetup}>
              <div className="form-group">
                <label>First Name</label>
                <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Your first name" required />
                {firstName.trim() && (
                  <span className="username-preview">This will appear as <strong>{firstName.trim()}'s Status</strong> on your public page</span>
                )}
              </div>
              <div className="form-group">
                <label>Username <span className="hint">(3-30 chars, no spaces)</span></label>
                <input type="text" value={username} onChange={e => setUsername(e.target.value.replace(/\s/g, ''))} placeholder="yourname123" maxLength={30} required />
                {usernameStatus && (
                  <span className={`username-status ${usernameStatus}`}>
                    {usernameStatus === 'checking' && 'Checking...'}
                    {usernameStatus === 'available' && '✓ Available'}
                    {usernameStatus === 'taken' && '✗ Taken'}
                    {usernameStatus === 'invalid' && '3-30 chars, letters, numbers, underscores only'}
                  </span>
                )}
                {usernameStatus === 'available' && (
                  <span className="username-preview">trackthrough.com/{username.toLowerCase()}</span>
                )}
              </div>

              <div className="recovery-notice">
                <span className="recovery-icon">🔑</span>
                <span>Your security question and PIN below are used to recover your account if you forget your password. Please remember them.</span>
              </div>

              <div className="form-group">
                <label>Security Question</label>
                <select value={securityQuestion} onChange={e => setSecurityQuestion(e.target.value)}>
                  {SECURITY_QUESTIONS.map(q => <option key={q} value={q}>{q}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Security Answer</label>
                <input type="text" value={securityAnswer} onChange={e => setSecurityAnswer(e.target.value)} placeholder="Your answer" required />
              </div>
              <div className="form-group">
                <label>Recovery PIN <span className="hint">(3 digits)</span></label>
                <input type="text" value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 3))} placeholder="123" maxLength={3} required inputMode="numeric" />
              </div>
              <button type="submit" className="submit-btn" disabled={loading || usernameStatus !== 'available'}>
                {loading ? <span className="loading-spinner" /> : 'Get Started'}
              </button>
            </form>
          </div>
        )}

        {/* ─── Forgot Password ───────────────────────── */}
        {mode === 'forgot' && (
          <div className="login-card slide-up">
            {forgotStep === 0 && (
              <>
                <h2>Recover Your Account</h2>
                <p className="setup-subtitle">Enter your email to get started</p>
                <form onSubmit={handleForgotLookup}>
                  <div className="form-group">
                    <label>Email Address</label>
                    <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="you@example.com" required />
                  </div>
                  <button type="submit" className="submit-btn" disabled={loading}>
                    {loading ? <span className="loading-spinner" /> : 'Find My Account'}
                  </button>
                </form>
                <div className="divider"><span>or</span></div>
                <button className="google-btn" onClick={handleGoogleSignIn} disabled={loading}>
                  <Icons.Google /> <span>Sign in with Google instead</span>
                </button>
                <p className="switch-mode"><button onClick={() => { setMode('login'); setForgotStep(0); }}>Back to Sign In</button></p>
              </>
            )}

            {forgotStep === 1 && forgotProfile && (
              <>
                <h2>Verify Your Identity</h2>
                <p className="setup-subtitle">Answer your security question and enter your PIN</p>
                <form onSubmit={handleForgotVerify}>
                  <div className="form-group">
                    <label>{forgotProfile.securityQuestion}</label>
                    <input type="text" value={forgotAnswer} onChange={e => setForgotAnswer(e.target.value)} placeholder="Your answer" required />
                  </div>
                  <div className="form-group">
                    <label>Recovery PIN (3 digits)</label>
                    <input type="text" value={forgotPin} onChange={e => setForgotPin(e.target.value.replace(/\D/g, '').slice(0, 3))} placeholder="123" maxLength={3} required inputMode="numeric" />
                  </div>
                  <button type="submit" className="submit-btn" disabled={loading}>
                    {loading ? <span className="loading-spinner" /> : 'Verify Identity'}
                  </button>
                </form>
                <p className="switch-mode"><button onClick={() => setForgotStep(0)}>Back</button></p>
              </>
            )}

            {forgotStep === 2 && (
              <>
                <h2>Set New Password</h2>
                <p className="setup-subtitle">Identity verified. Choose a new password.</p>
                <form onSubmit={handleSetNewPassword}>
                  <div className="form-group">
                    <label>New Password <span className="hint">(min 6 characters)</span></label>
                    <div className="password-input">
                      <input type={showPassword ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••" minLength={6} required />
                      <button type="button" className="pw-toggle" onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <Icons.EyeOff size={16} /> : <Icons.Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Confirm New Password</label>
                    <input type="password" value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)} placeholder="••••••" required />
                  </div>
                  <button type="submit" className="submit-btn" disabled={loading}>
                    {loading ? <span className="loading-spinner" /> : 'Reset Password'}
                  </button>
                </form>
              </>
            )}

            {forgotStep === 3 && (
              <>
                <h2>Check Your Email</h2>
                <div className="magic-link-sent">
                  <div className="magic-icon">✉️</div>
                  <p>We've sent a password reset link to <strong>{forgotProfile?.email}</strong></p>
                  <p className="magic-hint">Click the link in your email to set your new password.</p>
                </div>
                <button className="submit-btn" onClick={() => { setMode('login'); setForgotStep(0); }}>
                  Back to Sign In
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
